import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import type { PMIssue, SchedulePreview } from "@/domains/pm/types";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { applyScheduleChange, previewScheduleChange } from "@/stores/pm/repository";
import { useIssueDependencies } from "./useIssueDependencies";

/** A date change waiting on the user's answer to "and everything downstream?". */
export interface PendingScheduleChange {
  /** Null for a staged batch, which has no single edited issue. */
  issue: PMIssue | null;
  start: string | null;
  due: string | null;
  preview: SchedulePreview;
  /** True when this is a whole staged arrangement rather than one edit. */
  batch?: boolean;
}

export interface DatePatch {
  start_date?: string | null;
  due_date?: string | null;
}

/**
 * The single path a date takes from any surface to the database.
 *
 * Every date-writing control in PM goes through this — the Gantt drag, the
 * detail-panel pickers, the list cells, the context menu, the create/edit
 * form. A schedule that only holds in one view is not a schedule, and a
 * dependency the list can quietly violate is not a dependency.
 *
 * Two round trips at most, and usually one:
 *
 *  * an issue with no successors cannot move anything, so it is written
 *    straight away. That is the overwhelming majority of edits, and it is why
 *    the dependency index is consulted before the server is.
 *  * otherwise the server is asked what WOULD happen. If nothing else moves,
 *    the write goes through unremarked; if something does, the user is shown
 *    what and decides.
 */
export function useScheduleChange(buildingId: number) {
  const issuesStore = useIssuesStore();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { dependencies } = useIssueDependencies(buildingId);
  const [pending, setPending] = useState<PendingScheduleChange | null>(null);
  const [applying, setApplying] = useState(false);

  const afterWrite = useCallback(
    (result: SchedulePreview) => {
      // Only reload when something OTHER than the edited issue moved. The
      // optimistic patch already covers the issue itself, and reloading on
      // every date edit would swap the rows out under a list the user is
      // working down.
      if (result.moved.length > 0 && result.cascaded !== false) {
        void issuesStore.loadIssues(buildingId);
      }
      void queryClient.invalidateQueries({ queryKey: ["pm-issue-dependencies", buildingId] });
    },
    [buildingId, issuesStore, queryClient]
  );

  const write = useCallback(
    async (issue: PMIssue, start: string | null, due: string | null, cascade: boolean) => {
      const patch = { start_date: start, due_date: due, schedule_pinned: true };
      issuesStore.updateIssueOptimistic(issue.id, patch);
      // Held until the RPC answers, so a reload that raced it (a colleague's
      // change, the chatbot) cannot paint the old dates back meanwhile.
      issuesStore.holdEdit(issue.id, patch);
      let result: SchedulePreview;
      try {
        result = await applyScheduleChange(issue.id, start, due, cascade);
      } catch (e) {
        issuesStore.releaseEdit(issue.id, patch);
        toast.error(friendlyIssueError(e, t));
        // The server is the only authority on what actually landed, and a
        // cascade may have written several rows before failing.
        void issuesStore.loadIssues(buildingId);
        return null;
      }
      issuesStore.releaseEdit(issue.id, patch);
      afterWrite(result);
      return result;
    },
    [afterWrite, buildingId, issuesStore, t]
  );

  /**
   * Ask for a date change. `patch` names only the ends that are changing; the
   * RPC is given the complete resulting pair, since a patch shape would make
   * null mean both "leave alone" and "clear this date".
   */
  const requestChange = useCallback(
    async (issue: PMIssue, patch: DatePatch) => {
      const start = "start_date" in patch ? (patch.start_date ?? null) : issue.start_date;
      const due = "due_date" in patch ? (patch.due_date ?? null) : issue.due_date;
      if (start === issue.start_date && due === issue.due_date) return;

      if (!dependencies.hasDependents(issue.id)) {
        await write(issue, start, due, true);
        return;
      }

      let preview: SchedulePreview;
      try {
        preview = await previewScheduleChange(issue.id, start, due);
      } catch (e) {
        toast.error(friendlyIssueError(e, t));
        return;
      }

      if (preview.moved.length === 0 && preview.warnings.length === 0) {
        await write(issue, start, due, true);
        return;
      }

      setPending({ issue, start, due, preview });
    },
    [dependencies, t, write]
  );

  const resolve = useCallback(
    async (cascade: boolean) => {
      if (!pending || !pending.issue) return;
      setApplying(true);
      await write(pending.issue, pending.start, pending.due, cascade);
      setApplying(false);
      setPending(null);
    },
    [pending, write]
  );

  const cancel = useCallback(() => setPending(null), []);

  /**
   * Write and cascade without asking. Bulk edits use this: one combined
   * preview would have to reconcile several independent cascades before any of
   * them was written, and prompting once per issue is not bulk editing.
   */
  const applyWithCascade = useCallback(
    (issue: PMIssue, start: string | null, due: string | null) => write(issue, start, due, true),
    [write]
  );

  return {
    requestChange,
    applyWithCascade,
    hasDependents: dependencies.hasDependents,
    pending,
    applying,
    resolve,
    cancel,
  };
}
